/**
 * Cloche de notifications. Toujours cliquable (toutes les pages dashboard).
 * - Staff (super_admin / admin avec accès dossiers) : derniers événements de
 *   dossier, liens vers /admin/projects/:id.
 * - Client : actions à faire — notamment « Choisissez votre créneau » quand BCL
 *   a proposé des dates, lien vers /projects/:id.
 * Un point rouge compte les nouveautés depuis la dernière ouverture.
 */
import { hasPermission } from '@bcl/shared';
import { Bell } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { listNotifications } from '@/features/admin/admin-api';
import {
  listAssignedAppointments,
  listMyAppointments,
} from '@/features/appointments/appointments-api';
import { statusLabels } from '@/features/projects/labels';
import { useAuth } from '@/lib/auth-context';

export const SEEN_KEY = 'bcl.notifs.seen';

type BellItem = { id: string; title: string; link: string; createdAt: string };

export function NotificationBell() {
  const { user } = useAuth();
  const role = user?.role;
  const isAdmin =
    !!user &&
    (role === 'super_admin' ||
      (role === 'admin' && hasPermission(role, user.permissions, 'projects.view')));
  const isClient = role === 'client';
  const isAssignee = role === 'technicien' || role === 'partenaire';

  const [items, setItems] = useState<BellItem[]>([]);
  const [open, setOpen] = useState(false);
  const [seen, setSeen] = useState(() =>
    Number(localStorage.getItem(SEEN_KEY) ?? 0),
  );

  useEffect(() => {
    let active = true;
    const load: Promise<BellItem[]> | null = isAdmin
      ? listNotifications().then((rows) =>
          rows.map((n) => ({
            id: n.id,
            title: `${n.reference ?? 'Dossier'} — ${statusLabels[n.status]}`,
            link: `/admin/projects/${n.projectId}`,
            createdAt: n.createdAt,
          })),
        )
      : isClient
        ? listMyAppointments().then((appts) =>
            appts
              .filter((a) => a.status === 'creneaux_proposes')
              .map((a) => ({
                id: a.id,
                title: `Choisissez votre créneau — ${a.projectReference ?? 'votre dossier'}`,
                link: `/projects/${a.projectId}`,
                createdAt: a.updatedAt,
              })),
          )
        : isAssignee
          ? listAssignedAppointments().then((appts) =>
              appts
                .filter((a) => a.status === 'attribue')
                .map((a) => ({
                  id: a.id,
                  title: `Dossier à répondre — ${a.projectReference ?? 'un dossier'}`,
                  link:
                    role === 'partenaire'
                      ? '/partenaire/rendez-vous'
                      : '/technicien/projects',
                  createdAt: a.updatedAt,
                })),
            )
          : null;
    if (load)
      load
        .then((b) => {
          if (active) setItems(b);
        })
        .catch(() => {});
    return () => {
      active = false;
    };
  }, [isAdmin, isClient]);

  const unseen = items.filter(
    (n) => new Date(n.createdAt).getTime() > seen,
  ).length;

  function toggle() {
    const next = !open;
    setOpen(next);
    if (next) {
      const now = Date.now();
      localStorage.setItem(SEEN_KEY, String(now));
      setSeen(now);
    }
  }

  return (
    <div className="relative">
      <button
        onClick={toggle}
        aria-label="Notifications"
        className="relative rounded-md p-2 text-gray-500 hover:bg-gray-100"
      >
        <Bell className="h-5 w-5" />
        {unseen > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold text-white">
            {unseen}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 z-50 mt-2 w-80 rounded-md border bg-white shadow-lg">
          <div className="border-b px-3 py-2 text-sm font-semibold">
            Notifications
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length > 0 ? (
              items.map((n) => (
                <Link
                  key={n.id}
                  to={n.link}
                  onClick={() => setOpen(false)}
                  className="block border-b px-3 py-2 text-sm last:border-b-0 hover:bg-gray-50"
                >
                  <div className="font-medium">{n.title}</div>
                  <div className="text-[11px] text-gray-400">
                    {new Date(n.createdAt).toLocaleString('fr-FR')}
                  </div>
                </Link>
              ))
            ) : (
              <p className="px-3 py-4 text-center text-sm text-gray-400">
                Aucune notification.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
